import { StaticImage } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";

import VideoCard from "../../components/cards/videoCard";
import Image from "../../components/image";
import ProgramHeaderStrip from "../../components/program/ingo/programHeaderStrip";
import TravelFlexibility from "../../components/program/travelFlexibility";
import CtaButton from "../../components/shared/buttons/ctaButton";
import ProgramRating from "../../components/shared/programRating";
import { useTheme } from "../../context/ThemeContext";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import ArrowRight from "../../images/icons/arrow-right.inline.svg";
import PhoneDark from "../../images/icons/phone-icon-dark.inline.svg";
import PhoneLight from "../../images/icons/phone-icon-light.inline.svg";
import ShareIcon from "../../images/icons/share-icon.inline.svg";
import ViewMoreButton from "../../images/icons/view-more-icon.inline.svg";
import BackFlower from "../../images/illustrations/back-flower.inline.svg";
import MountainIllustrationGray from "../../images/illustrations/Mountain_illustration_gray.inline.svg";
import { convertToSlug } from "../../utils/convertToSlug";
import { Link } from "../../utils/customLink";
import { shareIcons } from "../../utils/shareIcons";
import ContactForm from "../forms/contact";
import Modal from "../shared/modal";
import ProgramSummaryDetails from "./programSummaryDetails";

const ProgramSummary = ({
  programName,
  programTitle,
  programSubTitle = "",
  programDuration,
  programFocus,
  programFeaturedVideo,
  programFeaturedImage,
  programGalleryFeaturedImage = {},
  programUri,
  programId,
  gallery,
  programCode,
  programPopular,
  programLocation,
  programCapacity,
  programFeaturedVideoTestimonials,
  programType = "",
  programTypes = "",
  groupInstitute = "",
  fundSpendingStatusText = "",
  openModal,
  setOpenModal,
  setBrochureDownloadModal,
  formType = "",
  formSubheading,
  formHeading,
  setActiveTabSlidebySlug,
  liveUpdates = [],
  programProviderBio = {},
  sdgIcons = [],
  travelDetails = {},
  galleryCTALabel = "See photos",
  layout = "generic",
  containerClass = "summary-section flex flex-col items-center justify-center bg-generic-bg mx-auto pb-18 pt-8 px-2 relative",
  innerDetailContainerClass = "inner-detail-container justify-between md:items-center my-4 pb-8 sm:py-0 sm:border-0",
  bgIllustrationClass = "absolute z-0 hidden -bottom-80 -left-16 md:block stroke-white",
  innerContainer = "container flex flex-col w-11/12 sm:w-10/12",
  mainHeadingClass = "font-normal sm:dark:font-semibold",
  mainHeadingContainerClass = "flex items-center justify-between w-full",
  subHeadingClass = "py-2 font-light leading-6 sm:leading-[38px]",
  galleryContainerClass = "grid grid-flow-col grid-cols-4 grid-rows-2 gap-0 program-gallery sm:h-96 sm:gap-4",
  galleryViewMoreContainerClass = "flex flex-row justify-end py-4 sm:absolute sm:bottom-3 sm:right-3",
  featuredVideoClass = "bg-gray-dark",
  featuredGalleryItemClass = "dark:bg-gray-dark"
}) => {
  const [filteredGallery, setFilteredGallery] = useState([]);
  const [callbackRequest, setCallbackRequest] = useState("");

  const { colorMode, currentBrand } = useTheme();
  const [openShareWith, setOpenShareWith] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const breakpoint = useBreakpoint();

  const [, setDatesCostsViewed] = useState(() => {
    if (typeof window !== "undefined") {
      return window.localStorage.getItem("datesCostsViewed") === "true";
    }
    return false;
  });

  const shareWithMediaArr = shareIcons(shareUrl);

  const groupsRecordTypeId = "0123k000001QapgAAC";

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const hiddenFields = [{ fieldName: "program_id", fieldValue: programCode }];

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    setShareUrl(encodeURI(location?.href ?? "#"));
  }, []);

  useEffect(() => {
    if (gallery.length > 0) {
      const galleryLimitedList = gallery?.slice(0, 4);
      setFilteredGallery(galleryLimitedList);
    }
  }, [gallery]);

  useEffect(() => {
    const prepareCallbackRequest =
      programType === "group"
        ? `/enquiry-form/?type=group_callback&program=${programCode}&program_title=${programName}&record_type_id=${groupsRecordTypeId}`
        : programId
          ? `/enquiry-form/?type=specific_callback&program=${programId}&program_title=${programName}`
          : `/enquiry-form/?type=callback`;

    setCallbackRequest(prepareCallbackRequest);
  }, [gallery, programId, programName, programCode, programType]);

  const scrollToTabs = () => {
    const programTabs = document.querySelector("#view-tabs");
    programTabs?.scrollIntoView({ behavior: "smooth", block: "start" });

    const datesTab = convertToSlug("dates-and-prices");
    setActiveTabSlidebySlug(datesTab);

    // Set datesCostsViewed to true and store it in localStorage
    setDatesCostsViewed(true);
    window.localStorage.setItem("datesCostsViewed", "true");
  };

  const scrollToDatePricesAccordion = () => {
    const programAccordions = document.querySelector("#dates-and-prices");
    programAccordions?.scrollIntoView({ behavior: "smooth", block: "start" });

    const datesTab = convertToSlug("dates-and-prices");
    setActiveTabSlidebySlug(datesTab);
  };

  const scrollToReviews = () => {
    const programTabs = document?.querySelector("#view-tabs");
    programTabs?.scrollIntoView({ behavior: "smooth", block: "start" });

    const reviewsTab = convertToSlug("reviews");
    setActiveTabSlidebySlug(reviewsTab);

    //this is for mobile
    const selectedAccordion = document?.querySelector(`#${reviewsTab}`);
    selectedAccordion?.scrollIntoView({
      behavior: "auto",
      block: "start"
    });
  };

  const scrollToFlexTestimonials = () => {
    const programTestimonials = document.querySelector(
      "#flex-testimonial-section"
    );
    programTestimonials?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  const programIdentifier = programCode ?? programId;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  const applyNowUri =
    programType === "group"
      ? `/group-application-form/?type=application&program=${programIdentifier}&program_title=${programName}&group_institute=${groupInstitute}`
      : `/application-form/?${programSuffix}`;

  const programTypeList =
    programTypes && programTypes.length
      ? programTypes.map((pgmType) => pgmType?.title).join(", ")
      : "";

  const groupProgram = programType?.includes("group");
  const under18 = programType?.includes("under-18");

  const carbonFootprintStatus = liveUpdates?.carbonFootprintStatus ?? "";

  return (
    <>
      <section className={containerClass} style={{ gridArea: "1/1" }}>
        {layout === "journeys" ? (
          <div className="hidden md:block absolute top-0 left-0 bottom-0 right-0 bg-[#E8EDEB]">
            <MountainIllustrationGray
              className={`absolute z-10 flex items-end -bottom-0 bg-[#E8EDEB] w-full -left-[31.25rem] xl:h-3/5 2xl:h-3/4 2xl:left-0 2xl:w-2/5 `}
            />
          </div>
        ) : (
          <BackFlower className={bgIllustrationClass} />
        )}
        <div className={`z-10 ${innerContainer}`}>
          <div className="relative flex flex-col">
            <div className="relative flex flex-row justify-between">
              <div className="flex flex-col justify-left w-full">
                {layout === "generic" && (
                  <div className="sm:hidden pb-4 flex flex-row justify-between items-center">
                    <ProgramRating
                      outerContainerClass={"items-center md:pl-7"}
                      darkRatingIconClass="sm:hidden block mt-1 fill-white"
                    />
                  </div>
                )}
                <div className={mainHeadingContainerClass}>
                  <h1
                    className={mainHeadingClass}
                    dangerouslySetInnerHTML={{
                      __html: programName
                    }}
                  />
                  {layout === "journeys" && (
                    <>
                      <h7
                        className="uppercase text-lg font-semibold text-black-lighter mb-5 pl-2 md:pl-4 border-l-5 border-l-red-light"
                        dangerouslySetInnerHTML={{ __html: programSubTitle }}
                      />

                      <h2
                        className={subHeadingClass}
                        dangerouslySetInnerHTML={{ __html: programTitle }}
                      />
                    </>
                  )}
                  {layout === "generic" && (
                    <div className="relative flex items-center cursor-pointer justify-right">
                      {!openShareWith && (
                        <ShareIcon
                          onClick={() => setOpenShareWith(!openShareWith)}
                          className={
                            "fill-black-lighter dark:fill-gray-generic hover:dark:fill-white hover:fill-black-lighter opacity-60 hover:opacity-100"
                          }
                        />
                      )}
                      {openShareWith && (
                        <div className="absolute -top-[14px] -left-[32px]">
                          <ul
                            className="flex flex-col gap-2 border border-gray-lighter rounded-xxxxl p-3 dark:bg-gray-dark"
                            style={{
                              boxShadow: "0px 4px 40px rgba(0, 0, 0, 0.25)"
                            }}
                          >
                            <li>
                              <ShareIcon
                                onClick={() => setOpenShareWith(!openShareWith)}
                                className={
                                  "fill-black-lighter dark:fill-gray-generic hover:dark:fill-white hover:fill-black-lighter opacity-60 hover:opacity-100 w-5 h-5"
                                }
                              />
                            </li>
                            {shareWithMediaArr &&
                              shareWithMediaArr.length > 0 &&
                              shareWithMediaArr.map((media, idx) => {
                                return <li key={idx}>{media.node}</li>;
                              })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {layout === "generic" && (
                  <h2
                    className={subHeadingClass}
                    dangerouslySetInnerHTML={{ __html: programTitle }}
                  />
                )}
              </div>
              {layout === "generic" && (
                <div className="absolute right-0 flex items-center sm:static justify-right -bottom-16 sm:bottom-0">
                  <StaticImage
                    src="../../images/share-btn.svg"
                    alt="Share Button"
                    className="share-btn object-contain object-center !hidden"
                  />
                </div>
              )}
            </div>

            <div className={innerDetailContainerClass}>
              {layout === "generic" && (
                <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:items-center sm:pb-4 order-2 sm:order-1">
                  <div className="program-callback flex flex-col sm:flex-row sm:items-center sm:gap-5 grow">
                    {/* <GviFlexIconStrip
                        outerContainerClass="p-0.5 rounded-r-md flex bg-gradient-to-r from-transparent from-0% via-yellow-standard via-50% to-black-lighter dark:from-gray-darker dark:via-gray-denary dark:to-gray-denary sm:bg-none dark:sm:bg-none sm:p-0"
                        containerClass="w-full sm:w-auto flex flex-row items-center gap-0 pt-0 sm:pt-0 px-2 sm:px-0 flex flex-row w-full items-start rounded-r-md border-l-0 rounded-none bg-gradient-to-r from-gray-lighten from-20% via-yellow-standard via-40% to-black-lighter dark:from-20% dark:via-70% dark:from-gray-darker dark:via-gray-quadrodecimal dark:to-gray-quadrodecimal sm:bg-none dark:sm:bg-none"
                        emblemContainerClass="rounded-full z-20 flex items-center justify-center bg-gray-lighten dark:bg-gray-darker"
                        emblemInnerClass=""
                        emblemIconClass="fill-white dark:fill-white"
                        outerDescriptionContainerClass={
                          "sm:rounded-r-md border-l-0 rounded-none p-0.5 sm:bg-gradient-to-r sm:from-transparent sm:via-yellow-standard sm:to-black-lighter to-80% sm:dark:via-gray-denary sm:dark:to-gray-denary"
                        }
                        descriptionClass="text-white font-alice dark:font-body sm:text-center py-5 px-7 xs:p-5 sm:py-1 sm:px-10 sm:rounded-r-md sm:-ml-5 z-10 flex-1 sm:bg-gradient-to-r sm:from-transparent sm:from-20% sm:via-yellow-standard sm:via-60% sm:to-black-lighter sm:dark:from-gray-darker sm:dark:via-gray-quadrodecimal sm:dark:to-gray-quadrodecimal "
                        stripLogo={stripLogo}
                      /> */}

                    <div className="divide-y height-divider mt-6 mb-2 border-0.5 border-gray-standard sm:hidden" />

                    <ProgramSummaryDetails
                      programCode={programCode}
                      programDuration={programDuration}
                      programTypeList={programTypeList}
                      programTypes={programTypes}
                      programPopular={programPopular}
                      programCapacity={programCapacity}
                      summaryDetailsClass={`flex flex-nowrap flex-col sm:flex sm:flex-col pb-2 xs:gap-2`}
                      currentBrand={currentBrand}
                      carbonFootprintStatus={carbonFootprintStatus}
                      scrollToReviews={scrollToReviews}
                    />
                    <div className="divide-y height-divider mt-4 mb-2 border-0.5 border-gray-standard sm:hidden" />

                    <div
                      className={`flex flex-col xxxs:flex-col sm:flex-row xxxs:flex-wrap ${groupProgram || under18 ? "sm:flex-wrap" : "sm:flex-nowrap"} sm:gap-5 grow sm:justify-end`}
                    >
                      {!groupProgram && travelDetails?.title?.length > 0 && (
                        <div className="block sm:hidden py-6">
                          <TravelFlexibility
                            travelDetails={travelDetails}
                            isGVI={true}
                            hasBullets={false}
                            outerContainerClass=""
                            innerContainerClass=""
                            headingClass="dark:text-white"
                            listItemClass="text-xs"
                          />
                        </div>
                      )}

                      {(groupProgram || under18) && (
                        <div className="flex justify-end items-center w-full min-w-max md:w-auto">
                          <Link
                            className="flex sm:hidden justify-center items-center w-full md:w-auto font-bold sm:font-normal py-2 px-6 mt-5 md:mt-0 dark:pr-10 dark:!px-6 border border-black-lighter dark:border-white rounded-full dark:rounded-sm bg-white dark:bg-transparent text-black-lighter dark:text-white"
                            to={`/parent-consultation/?program_uri=${shareUrl}&program_code=${programCode}`}
                            rel={"nofollow"}
                          >
                            Free parent consultation
                          </Link>
                          <button
                            className="hidden sm:flex justify-center items-center w-full md:w-auto font-bold sm:font-normal py-2 px-6 mt-5 md:mt-0 dark:pr-10 dark:!px-6 border border-black-lighter dark:border-white rounded-full dark:rounded-sm bg-white dark:bg-transparent text-black-lighter dark:text-white"
                            onClick={() => {
                              setOpenModal(true);
                            }}
                          >
                            Free parent consultation
                          </button>
                        </div>
                      )}

                      {!groupProgram && (
                        <button
                          className="flex justify-center items-center w-full min-w-max md:w-auto font-bold sm:font-normal py-2 px-6 mt-5 md:mt-0 dark:pr-10 dark:!px-6 border border-black-lighter dark:border-white rounded-full dark:rounded-sm bg-white dark:bg-transparent text-black-lighter dark:text-white"
                          onClick={
                            breakpoint === "xs"
                              ? scrollToDatePricesAccordion
                              : scrollToTabs
                          }
                        >
                          <div className="flex lowercase md:normal-case">
                            <span className="md:hidden normal-case">
                              View &nbsp;
                            </span>{" "}
                            Dates & prices
                          </div>
                        </button>
                      )}

                      {groupProgram && (
                        <Link
                          className="flex justify-center items-center w-full min-w-max md:w-fit font-bold sm:font-normal py-2 pr-5 pl-3 mt-5 md:mt-0 dark:pr-10 dark:!px-6 border border-black-lighter dark:border-white rounded-full dark:rounded-sm bg-white dark:bg-transparent text-black-lighter dark:text-white"
                          to={callbackRequest}
                        >
                          Call me back
                          <span className="flex items-center pl-2">
                            {colorMode === "light" ? (
                              <PhoneDark className="w-5 h-5 mx-auto group-hover:fill-white" />
                            ) : (
                              <PhoneLight className="w-5 h-5 mx-auto group-hover:fill-black-lighter" />
                            )}
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(layout === "ingo" || layout === "journeys") && (
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                  <ProgramHeaderStrip
                    programCode={programCode}
                    programDuration={programDuration}
                    programTypeList={programTypeList}
                    programTypes={programTypes}
                    programPopular={programPopular}
                    programCapacity={programCapacity}
                    programProviderBio={programProviderBio}
                    programLocation={programLocation}
                    sdgIcons={sdgIcons}
                    currentBrand={currentBrand}
                    carbonFootprintStatus={carbonFootprintStatus}
                    scrollToReviews={scrollToFlexTestimonials}
                    containerClass={`${layout === "journeys" ? "gap-2" : "gap-y-4 gap-x-10"} flex flex-row flex-wrap sm:gap-2 sm:items-center`}
                    summaryBlockItemTextClass={
                      layout === "journeys" ? "" : "text-white"
                    }
                    summaryBlockItemSpacingClass={
                      layout === "journeys" ? "" : "py-2 sm:py-0"
                    }
                    fundSpendingStatus={fundSpendingStatusText}
                  />
                  {layout === "journeys" && (
                    <>
                      <div className="justify-end py-1 hidden sm:flex w-1/4">
                        <CtaButton
                          preset="secondary"
                          ctaLabel="Download itinerary"
                          showArrowIcon={false}
                          showAsButton={true}
                          labelClass="px-2 sm:px-8 text-base leading-genericCta font-bold"
                          onClick={() => setBrochureDownloadModal(true)}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {openModal && (
              <Modal
                closeModal={handleCloseModal}
                modalContentStyles="w-full sm:w-2/3 mx-auto bg-modal-secondary-light dark:bg-modal-secondary-dark gvi-border-light border-0.5 border-gray-testimonial rounded-xxs py-3"
                modalContainerStyles="w-11/12 sm:w-10/12"
                modalOuterContainerStyles={
                  "absolute sm:fixed top-0 bottom-0 left-0 right-0 z-30 flex items-center justify-center w-full h-full modalBackground gvi-modal-light-bg dark:gvi-modal-bg"
                }
                modalCloseBtnStyles="w-full flex justify-left h-auto pl-2"
                modalContent={
                  <div className="px-4 sm:px-14 pb-8">
                    <ContactForm
                      formType={formType}
                      formHeading={formHeading}
                      formSubheading={formSubheading}
                      formStyles="rounded-md w-full mx-auto"
                      inputStyles="contact-form-input !bg-transparent"
                      ctaLabel="Apply"
                      hiddenFields={hiddenFields}
                      contactLabelsClass="text-white"
                      iconColor={"text-white fill-white"}
                    />
                  </div>
                }
              />
            )}

            {layout === "generic" && (
              <div className="flex flex-row items-center justify-between py-2 mt-3 mb-6 sm:hidden">
                <div className="flex items-center justify-center w-full h-auto py-2 rounded-full dark:rounded-sm bg-red-light dark:gvi-bg-green">
                  <a
                    className="block w-full font-bold text-white dark:text-black"
                    href={applyNowUri}
                  >
                    <div className="flex items-center justify-center">
                      <div className="pr-2 text-white dark:text-black">
                        Apply now
                      </div>
                      <ArrowRight className="w-3 h-3 stroke-white dark:stroke-black" />
                    </div>
                  </a>
                </div>
              </div>
            )}

            {/* if layout is ingo, do not show gallery on mobile, so we need breakpoint to come into play here */}
            {(filteredGallery || programFeaturedVideo) && (
              <>
                <div className={galleryContainerClass}>
                  {programFeaturedVideo ? (
                    <div
                      className={`z-20 h-auto col-span-4 row-span-1 mb-2 text-white rounded-md project-video featured-video sm:col-span-2 sm:row-span-2 sm:mb-0 ${featuredVideoClass} `}
                      dangerouslySetInnerHTML={{
                        __html: programFeaturedVideo
                      }}
                    />
                  ) : (
                    (programGalleryFeaturedImage || programFeaturedImage) && (
                      <div className="col-span-4 row-span-1 text-white rounded-md  sm:col-span-2 sm:row-span-2 w-full h-full relative shadow-lg">
                        <Image
                          imageClass={`z-0 h-full bg-white object-cover object-center rounded-md w-full absolute top-0 bottom-0 left-0 right-0 ${featuredVideoClass} `}
                          sourceUrl={
                            programGalleryFeaturedImage ?? programFeaturedImage
                          }
                        />
                        <div className="z-10 absolute top-0 right-0 h-full w-full bottom-0 left-0 rounded-md border-6 border-white/20"></div>
                      </div>
                    )
                  )}

                  {programFeaturedVideoTestimonials &&
                    programFeaturedVideoTestimonials
                      ?.slice(0, 2)
                      .map((videoUrl, idx) => {
                        const useVideo = videoUrl.programFeaturedVideo;
                        return (
                          <div
                            key={`feat-video-item-${idx}`}
                            className={`${featuredGalleryItemClass} rounded-xs hidden md:block py-[8px]  col-span-2 sm:col-span-1`}
                          >
                            <VideoCard
                              key={idx}
                              featVideos={useVideo}
                              cardClass={`${idx === 0 ? "mr-1 sm:mr-0" : ""} ${
                                idx === 1 ? "ml-1 sm:ml-0" : ""
                              }  h-48 sm:h-full object-cover object-center w-full`}
                            />
                          </div>
                        );
                      })}

                  {filteredGallery &&
                    filteredGallery
                      ?.slice(
                        0,
                        programFeaturedVideoTestimonials &&
                          programFeaturedVideoTestimonials?.length > 0
                          ? programFeaturedVideoTestimonials?.length >= 2
                            ? 2
                            : 3
                          : breakpoint === "xs"
                            ? 2
                            : 4
                      )
                      ?.map((galleryItem, idx) => {
                        const {
                          localFile: imageSrc,
                          sourceUrl,
                          gatsbyImage
                        } = galleryItem;

                        return (
                          <Image
                            key={`gitem-${idx}`}
                            imageAlt={""}
                            image={gatsbyImage}
                            imageClass={`${idx === 0 ? "mr-1 sm:mr-0" : ""} ${
                              idx === 1 ? "ml-1 sm:ml-0" : ""
                            }  h-48 sm:h-full w-full object-cover object-center  rounded-xs col-span-2 sm:col-span-1`}
                            sourceUrl={
                              imageSrc &&
                              imageSrc?.extension &&
                              imageSrc?.publicURL
                                ? imageSrc?.publicURL
                                : sourceUrl
                            }
                          />
                        );
                      })}
                </div>
                <div className={galleryViewMoreContainerClass}>
                  {filteredGallery?.length > 0 && (
                    <div className="w-full sm:w-auto">
                      <Link
                        className="callback-button group flex py-2 px-2 sm:px-3 justify-between items-center bg-white dark:bg-transparent sm:gvi-blur-bg !text-base sm:text-white sm:border sm:border-white sm:rounded-full dark:sm:rounded-sm dark:hover:!bg-primary-bg"
                        to={`gallery/${programUri}`}
                        rel={"nofollow"}
                      >
                        <div
                          className="text-mob sm:text-base font-bold sm:font-normal m-auto sm:m-0 text-black-lighter dark:text-white sm:text-white dark:group-hover:text-white"
                          dangerouslySetInnerHTML={{
                            __html: galleryCTALabel
                          }}
                        />

                        <span className="items-center hidden pl-2 sm:flex">
                          <ViewMoreButton className="object-contain object-center view-more fill-white dark:group-hover:fill-white" />
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProgramSummary;
